import { forwardRef, ForwardRefRenderFunction } from "react";
//#region types
import { ConnectInvoiceFormProps } from "models/propTypes/timeReportingProjectPropTypes";
import {
  EditableTableFieldProps,
  FormFieldDefinitionType,
  FormHandles,
  GenericFormSection,
  WizardDialogProps,
} from "models/propTypes/genericFormProps";
import { FormEnums } from "models/enums";
import { TranslationKeys } from "translations";
import { TimeReportingProjectFormTypes } from "models/formTypes";
import { FormFieldConstants, IconDefinitionConstants } from "utils/constants";
import { LocalizationHelperInstance, UtilsHelper } from "utils/helpers";
import cssExports from "theme/styles/main.module.scss";
//#endregion
//#region components
import { GenericForm, Icon } from "components/common";
import StepOne from "./stepOne";
import { Grid } from "@mui/material";
//#endregion

const ConnectInvoiceForm: ForwardRefRenderFunction<FormHandles, ConnectInvoiceFormProps> = (props, ref) => {
  const {
    onSuggestionsSearchTextChange,
    onAllSearchTextChange,
    onInvoiceSelect,
    memberCategories,
    project,
    getInvoiceItems,
    defaultFilter,
    onLeftToInvoiceChange,
  } = props;

  const getMemberCategoryFields = () => {
    const memberGroup = UtilsHelper.groupBy(
      memberCategories,
      FormFieldConstants.TimeReportingProject.ConnectInvoiceMemberCategory.MemberId
    );

    const fields: GenericFormSection<any>[] = memberGroup.map((mc, index) => ({
      label: mc.value[0].memberName ?? "",
      type: FormEnums.FormSectionType.SIMPLETAB,
      innerFields: {
        [`${mc.key}`]: {
          controlLabel: TranslationKeys.SalaryDeviation.TypeOfAllowance,
          label: "",
          type: FormEnums.FormFieldType.EDITABLETABLE,
          overrideHideErrorBubble: true,
          innerFields: {
            icon: {
              label: "",
              type: FormEnums.FormFieldType.CUSTOM,
              widthColumnCount: 1,
              customField: (
                <Grid container className={cssExports.categoryIcon}>
                  <span className={cssExports.icon}>
                    <Icon icon={IconDefinitionConstants.WList} />
                  </span>
                </Grid>
              ),
            },
            categoryName: {
              readonly: true,
              label: "",
              type: FormEnums.FormFieldType.TEXT,
            },
            amount: {
              label: TranslationKeys.Common.Amount,
              icon: IconDefinitionConstants.WCalculator,
              type: FormEnums.FormFieldType.CURRENCY,
              extraProps: {
                currencyReferenceFieldName: FormFieldConstants.TimeReportingProject.ConnectInvoice.Currency,
              },
              widthColumnCount: 4,
            },
          } as FormFieldDefinitionType<TimeReportingProjectFormTypes.ConnectInvoiceCategory>,
          extraProps: {
            hideActions: true,
          } as EditableTableFieldProps<TimeReportingProjectFormTypes.ConnectInvoiceCategory>,
        },
      },
    }));
    return fields;
  };

  return (
    <GenericForm<TimeReportingProjectFormTypes.ConnectInvoice>
      formRef={ref}
      {...props}
      fields={[
        {
          label: TranslationKeys.TimeReportingProject.ChooseInvoice,
          descriptionText: TranslationKeys.TimeReportingProject.ChooseInvoiceDescription,
          type: FormEnums.FormSectionType.DIALOGWIZARD,
          icon: IconDefinitionConstants.WSend,
          innerFields: {
            selectedItems: {
              controlLabel: "",
              label: "",
              customField: (
                <StepOne
                  onItemSelect={onInvoiceSelect}
                  onSuggestionsSearchTextChange={onSuggestionsSearchTextChange}
                  onAllSearchTextChange={onAllSearchTextChange}
                  project={project}
                  getInvoiceItems={getInvoiceItems}
                  defaultFilter={defaultFilter}
                ></StepOne>
              ),
              type: FormEnums.FormFieldType.CUSTOM,
            },
          },
        },
        {
          label: TranslationKeys.TimeReportingProject.DivideAmount,
          descriptionText: TranslationKeys.TimeReportingProject.DivideAmountDescription,
          type: FormEnums.FormSectionType.DIALOGWIZARD,
          icon: IconDefinitionConstants.WSplit,
          innerFields: getMemberCategoryFields(),
          className: cssExports.divideAmountWizard,
          extraProps: {
            leftSummaryValue: (values: TimeReportingProjectFormTypes.ConnectInvoice) => {
              let total = 0;
              Object.entries(values).forEach(([key, value]) => {
                if (Number(key)) {
                  total =
                    total +
                    (value as TimeReportingProjectFormTypes.ConnectInvoiceCategory[])
                      .map((c) => c.amount ?? 0)
                      .reduce((a, b) => +a + +b, 0);
                }
              });
              onLeftToInvoiceChange((values.invoiceAmount ?? 0) - total);
              return LocalizationHelperInstance.getFormattedCurrency(
                (values.invoiceAmount ?? 0) - total,
                values.currency
              );
            },
            leftSummaryText: LocalizationHelperInstance.getFormattedMessage(
              TranslationKeys.TimeReportingProject.DialogSummaryText
            ),
          } as WizardDialogProps,
        },
      ]}
      hideSuccess
      hideErrorBubble
    />
  );
};

export default forwardRef(ConnectInvoiceForm);
